import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import getDomain from '@unifyapps/network/utils/url/getDomain';
import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import isEqual from 'react-fast-compare';
import { EMPTY_OBJECT } from '../../../consts/empty';
import { useBlockRuntimeData, useRowScopeStack } from '../../context/BlockRuntimeStateProvider';
import { useDependencyGraphProvider } from '../../context/DependencyGraphProvider';
import { useGetPageSubscribeToDependencyUpdates } from '../../context/DependencyGraphProvider/hooks/useGetSubscribeToDependencyUpdates';
import { useRuntimeBlockPreComputationContext } from '../../context/RuntimeBlockPreComputationProvider/context';
import { useSlotContextSnapshots } from '../../context/SlotContextSnapshotsProvider/SlotContextSnapshotsContext';
import {
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../../stores/app/GlobalAppStateStore';
import { useGetGlobalStateStoreState, useGlobalStateStore } from '../../stores/GlobalStateStore';
import type { SlotContextSnapshot } from '../../stores/GlobalStateStore/types/slotContext';
import { useGetInterfaceStoreState, useInterfaceStore } from '../../stores/InterfaceStore';
import InterfaceHelper from '../../stores/InterfaceStore/helper/InterfaceHelper';
import {
  useGetWalkthroughComputeContext,
  useWalkthroughComputeContext,
} from '../../stores/WalkthroughManagerStore/hooks';
import type { RowScope } from '../../utils/repeatables/attachRowScopeIdentities';
import type { ComputeContextMode } from './const';
import ContextBuilder from './context/ContextBuilder';
import ContextFactory from './context/ContextFactory';
import type { ComputeContextType } from './context/types';
import { useGetPageInterfaceEntityMethods } from './useGetInterfaceEntityMethods';
import { useGetNoCodeComputedData } from './useGetNoCodeComputedData';
import { usePageInterfaceEntityMethods } from './useInterfaceEntityMethods';
import { useRepeatableActions } from './useRepeatableActions';
import { createBuildRuntimeSourceInfo } from './utils/createBuildRuntimeSourceInfo';
import { getInterfaceRecordForComputeContext } from './utils/getInterfaceRecordForComputeContext';
import { getIsDependentContextChanged } from './utils/getIsDependentContextChanged';
import { mergeSlotContextSnapshots } from './utils/mergeSlotContextSnapshots';

export type { ComputeContextMode } from './const';

export type GetComputeContextBuilder = () => ComputeContextType;
export type UseReactiveComputeContextType = ({ mode }: { mode?: ComputeContextMode }) => {
  context: ComputeContextType;
};
export type UseNonReactiveComputeContextType = ({
  entityId,
  key,
  blocksRuntimeState,
  slotContextSnapshots,
  mode,
  rowScopeStack,
}: {
  entityId: string | null;
  key?: string;
  blocksRuntimeState?: Record<string, Record<string, unknown>>;
  slotContextSnapshots?: Record<string, SlotContextSnapshot>;
  mode?: ComputeContextMode;
  rowScopeStack?: readonly RowScope[];
}) => { context: ComputeContextType };

/**
 * we created this hook that takes blocksRuntimeState in params because we observed that the context was causing
 * re-renders after the initial mount, which was leading to recalculation of the context, that was adding more overhead
 * to the page load time.
 */
function useComputeContextBuilder({
  blocksRuntimeState,
  slotContextSnapshots,
  mode,
  rowScopeStack,
  entityId,
}: {
  blocksRuntimeState?: Record<string, Record<string, unknown>>;
  slotContextSnapshots?: Record<string, SlotContextSnapshot>;
  mode: ComputeContextMode;
  rowScopeStack?: readonly RowScope[];
  entityId?: string | null;
}) {
  const contextBuilder = useMemo(() => new ContextBuilder(), []);
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();

  // useRef (NOT useMemo) so the closure-scoped cache survives across dep changes;
  // useMemo would regenerate the factory and defeat reference-stability reuse.
  const buildRuntimeSourceInfoRef = useRef(createBuildRuntimeSourceInfo());

  // ref so the `callBlockMethod` closure (built before the builder fn is in scope)
  // can read the freshest context at dispatch time — otherwise binding expressions
  // in methodPayload would resolve against an empty actionContext.
  const computeContextBuilderRef = useRef<GetComputeContextBuilder | null>(null);

  // instanceId falls through to the hook's entityId for multi-page-instance
  // pages (copilot threads, embedded apps); explicit arg overrides.
  const repeatableActions = useRepeatableActions({
    mode,
    getLiveContext: () => computeContextBuilderRef.current?.(),
    defaultInstanceId: entityId,
  });

  const getComputeContextForMethods = useCallback(
    () => computeContextBuilderRef.current?.() ?? {},
    [],
  );

  const {
    getActionsForComputeContext,
    getBlockMethodsForComputeContext,
    getDataSourceMethodsForComputeContext,
    getLocalStorageMethodsForComputeContext,
    getPageVariableMethodsForComputeContext,
    getSessionStorageMethodsForComputeContext,
    getPageFunctionMethodsForComputeContext,
  } = useGetPageInterfaceEntityMethods({
    mode,
    getComputeContext: getComputeContextForMethods,
  });
  const { getWalkthroughComputeContext } = useGetWalkthroughComputeContext();
  const queryMetaContext = useGetQueryMeta();
  const domain = useMemo(() => getDomain(queryMetaContext), [queryMetaContext]);

  const { blockIdVsClosestRuntimeSourceBlockAncestor, runtimeSourceBlockIds } =
    useRuntimeBlockPreComputationContext();

  const getQueryMeta = useCallback(
    (interfaceRecord: InterfaceEntity) => {
      const basePath =
        queryMetaContext?.basePath || InterfaceHelper.getInterfaceBasePath(interfaceRecord);

      return { domain, basePath, apiDomain: domain };
    },
    [queryMetaContext, domain],
  );

  const getComputeContextBuilder = useCallback<GetComputeContextBuilder>(() => {
    const globalState = getGlobalStateStoreState();
    const interfaceState = getInterfaceStoreState();
    const activeAppVariantKey = getGlobalAppStateStoreState().activeAppVariantKey;
    const appGlobalState = getGlobalAppStateStoreState();
    const blockMethods = getBlockMethodsForComputeContext();
    const dataSourceMethods = getDataSourceMethodsForComputeContext();
    const pageVariableMethods = getPageVariableMethodsForComputeContext();
    const pageFunctionMethods = getPageFunctionMethodsForComputeContext();
    const localStorageMethods = getLocalStorageMethodsForComputeContext();
    const actions = getActionsForComputeContext();
    const sessionStorageMethods = getSessionStorageMethodsForComputeContext();
    const walkthrough = getWalkthroughComputeContext();

    const interfaceRecordForContext = getInterfaceRecordForComputeContext({
      appGlobalState,
      queryBasePath: queryMetaContext?.basePath,
      interfaceRecord: interfaceState.interfaceRecord,
      interfaceMode: interfaceState.mode,
    });

    // merge short-circuits to the same blocks ref when nothing changed, so
    // ContextBuilder.canReuseBaseContext stays cache-hot
    const mergedBlocks = mergeSlotContextSnapshots(
      globalState.blocks,
      slotContextSnapshots ?? EMPTY_OBJECT,
    );

    const runtimeSourceInfo = buildRuntimeSourceInfoRef.current(
      blockIdVsClosestRuntimeSourceBlockAncestor,
      mergedBlocks,
    );

    return ContextFactory.createPageContextBuilder(
      {
        state:
          mergedBlocks === globalState.blocks
            ? globalState
            : { ...globalState, blocks: mergedBlocks },
        appState: appGlobalState,
        interfaceRecord: interfaceRecordForContext,
        blocksRuntimeState,
        blockMethods,
        dataSourceMethods,
        pageVariableMethods,
        pageFunctionMethods,
        localStorageMethods,
        sessionStorageMethods,
        mediaUploads: globalState.mediaUploads,
        queryMeta: getQueryMeta(interfaceState.interfaceRecord),
        actions,
        activeAppVariantKey,
        walkthrough,
        runtimeSourceInfo,
        rowScopeStack,
        repeatableActions,
        runtimeSourceBlockIds,
      },
      contextBuilder,
    ).build();
  }, [
    getGlobalAppStateStoreState,
    getGlobalStateStoreState,
    getInterfaceStoreState,
    blocksRuntimeState,
    getBlockMethodsForComputeContext,
    getDataSourceMethodsForComputeContext,
    getPageVariableMethodsForComputeContext,
    getPageFunctionMethodsForComputeContext,
    getLocalStorageMethodsForComputeContext,
    getSessionStorageMethodsForComputeContext,
    getActionsForComputeContext,
    getQueryMeta,
    getWalkthroughComputeContext,
    contextBuilder,
    queryMetaContext,
    slotContextSnapshots,
    blockIdVsClosestRuntimeSourceBlockAncestor,
    rowScopeStack,
    repeatableActions,
    runtimeSourceBlockIds,
  ]);

  // wire in render (not effect) so the very first dispatch already sees the builder
  computeContextBuilderRef.current = getComputeContextBuilder;

  return {
    getComputeContextBuilder,
  };
}

export function useGetComputeContext({
  mode = 'readonly',
}: { mode?: ComputeContextMode; rowScopeStack?: readonly RowScope[] } = {}) {
  // must mirror withContext.tsx wiring: render-path and action-path share the
  // same row-scope stack source so a button inside a row dispatches against
  // the row's runtime instance, not the template.
  const rowScopeStack = useRowScopeStack();
  const { data: blocksRuntimeState } = useBlockRuntimeData();
  const slotContextSnapshots = useSlotContextSnapshots();
  const { getComputeContextBuilder } = useComputeContextBuilder({
    blocksRuntimeState,
    slotContextSnapshots,
    mode,
    rowScopeStack,
  });

  return { getComputeContext: getComputeContextBuilder };
}

export function useReactiveComputeContext({
  mode = 'readonly',
}: {
  mode?: ComputeContextMode;
} = {}) {
  const contextBuilder = useMemo(() => new ContextBuilder(), []);
  const { data: blocksRuntimeState } = useBlockRuntimeData();
  const blocks = useGlobalStateStore().hooks.useBlocks();
  const interfaceStore = useInterfaceStore();
  const interfaceRecord = interfaceStore.hooks.record.useDetails();
  const interfaceMode = interfaceStore.hooks.useMode();
  const permissions = useGlobalAppStateStore().hooks.usePermissions();
  const activeAppVariantKey = useGlobalAppStateStore().hooks.useActiveAppVariantKey();
  const blockRefs = useGlobalStateStore().hooks.useBlockRefs();
  const pageDataSourcesState = useGlobalStateStore().hooks.useDataSources();
  const pageDataSourcesMeta = useGlobalStateStore().hooks.useDataSourcesMeta();
  const appDataSourcesState = useGlobalAppStateStore().hooks.useDataSources();
  const appDataSourcesMeta = useGlobalAppStateStore().hooks.useDataSourcesMeta();
  const pageInputs = useGlobalStateStore().hooks.usePageInputs();
  const slotContextSnapshots = useSlotContextSnapshots();
  const pageParams = useGlobalStateStore().hooks.usePageParams();
  const location = useGlobalAppStateStore().hooks.useLocation();
  const userContext = useGlobalAppStateStore().hooks.useUserContext();
  const deviceContext = useGlobalAppStateStore().hooks.useDeviceContext();
  const appsAndIntegrations = useGlobalAppStateStore().hooks.useAppsAndIntegrations();
  const pageVariables = useGlobalStateStore().hooks.usePageVariables();
  const pageFunctions = useGlobalStateStore().hooks.usePageFunctions();
  const appPageVariables = useGlobalAppStateStore().hooks.usePageVariables();
  const appPageFunctions = useGlobalAppStateStore().hooks.usePageFunctions();
  const sessionStorage = useGlobalAppStateStore().hooks.useSessionStorage();
  const envVariables = useGlobalAppStateStore().hooks.useEnvVariables();
  const localStorage = useGlobalAppStateStore().hooks.useLocalStorage();
  const mediaUploads = useGlobalStateStore().hooks.useMediaUploads();
  const colorSchemeDetails = useGlobalAppStateStore().hooks.useColorSchemeDetails();
  const deviceDetails = useGlobalAppStateStore().hooks.useDeviceDetails();
  const appMode = useGlobalAppStateStore().hooks.useMode();
  const appNavigation = useGlobalAppStateStore().hooks.useNavigation();
  const queryMetaContext = useGetQueryMeta();
  const { walkthroughContext } = useWalkthroughComputeContext();
  const { blockIdVsClosestRuntimeSourceBlockAncestor, runtimeSourceBlockIds } =
    useRuntimeBlockPreComputationContext();

  const basePath =
    queryMetaContext?.basePath || InterfaceHelper.getInterfaceBasePath(interfaceRecord);

  const queryMeta = useMemo(
    () => ({ domain: getDomain(queryMetaContext), basePath }),
    [queryMetaContext, basePath],
  );

  // ref breaks the cycle: capturing `context` directly would make repeatableActions
  // depend on it, but `context`'s memo deps include repeatableActions.
  const reactiveContextRef = useRef<ComputeContextType | null>(null);

  // stable getter — reads freshest context at trigger() call time so scripts have
  // access to pageFunctions, pageVariables, blocks, etc.
  const getComputeContextForMethods = useCallback(() => reactiveContextRef.current ?? {}, []);

  // this is extremely important to be memoized and done outside the buildComputeContextFromState
  // keeping it inside causes +~500ms of more JS execution time on page load in employee portal of JFL
  // NOTE: So, all the code changes here has to be battle tested to ensure that this is not causing any performance issues or unintended regressions
  const {
    actions,
    blockMethods,
    dataSourceMethods,
    localStorageMethods,
    pageVariableMethods,
    sessionStorageMethods,
    pageFunctionMethods,
  } = usePageInterfaceEntityMethods({
    mode,
    getComputeContext: getComputeContextForMethods,
  });
  // Merge BEFORE aggregating into `state` so ContextBuilder's cache key stays
  // a single `state` reference; the merge util preserves `blocks` identity
  // when no snapshot applies, so cache hits in the no-slot-context case.
  const blocksWithSlotContext = useMemo(
    () => mergeSlotContextSnapshots(blocks, slotContextSnapshots ?? EMPTY_OBJECT),
    [blocks, slotContextSnapshots],
  );
  const state = useMemo(() => {
    return {
      blocks: blocksWithSlotContext,
      blockRefs,
      pageInputs,
      pageParams,
      pageVariables,
      pageFunctions,
      dataSources: pageDataSourcesState,
      dataSourcesMeta: pageDataSourcesMeta,
    };
  }, [
    blocksWithSlotContext,
    blockRefs,
    pageInputs,
    pageParams,
    pageVariables,
    pageFunctions,
    pageDataSourcesState,
    pageDataSourcesMeta,
  ]);

  const appState = useMemo(() => {
    return {
      location,
      userContext,
      deviceContext,
      appsAndIntegrations,
      localStorage,
      sessionStorage,
      pageVariables: appPageVariables,
      pageFunctions: appPageFunctions,
      colorSchemeDetails,
      envVariables,
      dataSources: appDataSourcesState,
      dataSourcesMeta: appDataSourcesMeta,
      deviceDetails,
      mode: appMode,
      navigation: appNavigation,
    };
  }, [
    location,
    userContext,
    deviceContext,
    appsAndIntegrations,
    localStorage,
    sessionStorage,
    appPageVariables,
    appPageFunctions,
    colorSchemeDetails,
    envVariables,
    appDataSourcesState,
    appDataSourcesMeta,
    deviceDetails,
    appMode,
    appNavigation,
  ]);

  const appGlobalStateForInterfaceContext = useMemo(
    () => ({
      interfaceRecord,
      activeAppVariantKey,
      deviceDetails,
      mode: appMode,
      permissions,
      navigation: appNavigation,
      location,
      basePath,
    }),
    [
      interfaceRecord,
      activeAppVariantKey,
      deviceDetails,
      appMode,
      permissions,
      appNavigation,
      location,
      basePath,
    ],
  );

  const interfaceRecordForContext = useMemo(
    () =>
      getInterfaceRecordForComputeContext({
        appGlobalState: appGlobalStateForInterfaceContext,
        queryBasePath: queryMetaContext?.basePath,
        interfaceMode,
      }),
    [appGlobalStateForInterfaceContext, queryMetaContext?.basePath, interfaceMode],
  );

  // useRef (NOT useMemo) so the closure-scoped cache survives across dep changes;
  // useMemo would regenerate the factory and defeat reference-stability reuse.
  const buildRuntimeSourceInfoRef = useRef(createBuildRuntimeSourceInfo());

  const runtimeSourceInfo = useMemo(
    () =>
      buildRuntimeSourceInfoRef.current(blockIdVsClosestRuntimeSourceBlockAncestor, state.blocks),
    [blockIdVsClosestRuntimeSourceBlockAncestor, state.blocks],
  );

  const repeatableActions = useRepeatableActions({
    mode,
    getLiveContext: () => reactiveContextRef.current,
  });

  const context = useMemo<ComputeContextType>(() => {
    return ContextFactory.createPageContextBuilder(
      {
        state,
        appState,
        interfaceRecord: interfaceRecordForContext,
        blocksRuntimeState,
        blockMethods,
        dataSourceMethods,
        pageVariableMethods,
        pageFunctionMethods,
        localStorageMethods,
        sessionStorageMethods,
        mediaUploads,
        queryMeta,
        actions,
        activeAppVariantKey,
        walkthrough: walkthroughContext,
        runtimeSourceInfo,
        repeatableActions,
        runtimeSourceBlockIds,
      },
      contextBuilder,
    ).build();
  }, [
    state,
    appState,
    interfaceRecordForContext,
    blocksRuntimeState,
    blockMethods,
    dataSourceMethods,
    pageVariableMethods,
    pageFunctionMethods,
    localStorageMethods,
    sessionStorageMethods,
    mediaUploads,
    queryMeta,
    actions,
    contextBuilder,
    activeAppVariantKey,
    walkthroughContext,
    runtimeSourceInfo,
    repeatableActions,
    runtimeSourceBlockIds,
  ]);

  reactiveContextRef.current = context;

  return { context };
}

// entityId you want to subscribe to, it should be present in the deps graph, for example can be blockId, dataSourceId,
// pageFuncId, etc.
// keys is used when u want to subscribe to an entity but from different locations
// key is used in case of repeatable like block where the entity id is constant but key will be changed based on the data length
// for example entityId: b_9kisj, key: runtimeBlock_b_9kisj_0
export function useNonReactiveComputeContext({
  entityId,
  key,
  blocksRuntimeState,
  slotContextSnapshots,
  mode = 'readonly',
  rowScopeStack,
}: {
  entityId: string | null;
  key?: string;
  blocksRuntimeState?: Record<string, Record<string, unknown>>;
  slotContextSnapshots?: Record<string, SlotContextSnapshot>;
  mode?: ComputeContextMode;
  rowScopeStack?: readonly RowScope[];
}) {
  const serverSnapshotRef = useRef<{
    context?: Record<string, unknown>;
    dependsOn?: DependsOnType[];
  }>(EMPTY_OBJECT);

  const snapshotRef = useRef<{
    context?: Record<string, unknown>;
    dependsOn?: DependsOnType[];
  }>(EMPTY_OBJECT);

  const { getComputeContextBuilder } = useComputeContextBuilder({
    blocksRuntimeState,
    slotContextSnapshots,
    mode,
    rowScopeStack,
    entityId,
  });

  const { graphManager, subscriptionManager } = useDependencyGraphProvider();
  const { subscribeToDependencyUpdates } = useGetPageSubscribeToDependencyUpdates();
  const { getNoCodeBulkComputedData } = useGetNoCodeComputedData();

  const getSnapshot = useCallback(() => {
    let lastDependsOn = snapshotRef.current.dependsOn;
    let prevContext = snapshotRef.current.context;

    const dependsOn = graphManager.getNode(entityId)?.dependsOn;

    // if no dependsOn, then no need to subscribe
    if (!dependsOn?.length) return;

    // first check if the dependency item is changed, then rerender
    const dependenciesChanged = !isEqual(dependsOn, lastDependsOn);
    if (dependenciesChanged) {
      lastDependsOn = dependsOn;
    }

    const latestContext = getComputeContextBuilder();
    const notifyingUpdateForId = subscriptionManager.getNotifyingUpdateForId();

    const { isContextChanged, nextContext } = getIsDependentContextChanged({
      prevContext,
      context: latestContext,
      dependsOn,
      notifyingUpdateForId,
      getNoCodeBulkComputedData,
    });

    if (isContextChanged) {
      prevContext = nextContext;
    }

    const shouldReRender =
      lastDependsOn !== snapshotRef.current.dependsOn ||
      prevContext !== snapshotRef.current.context;

    if (shouldReRender) {
      snapshotRef.current = {
        context: prevContext,
        dependsOn,
      };
    }

    return snapshotRef.current;
  }, [
    graphManager,
    entityId,
    getComputeContextBuilder,
    subscriptionManager,
    getNoCodeBulkComputedData,
  ]);

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!entityId) {
        return () => {
          // noop
        };
      }

      // subscribe to dependency updates for the given entityIds
      const unsubscribe = subscribeToDependencyUpdates({
        id: entityId,
        callback,
        key,
      });

      return () => {
        unsubscribe();
      };
    },
    [entityId, key, subscribeToDependencyUpdates],
  );

  const getServerSnapshot = useCallback(() => {
    if (!entityId) {
      return;
    }

    const dependsOn = graphManager.getNode(entityId)?.dependsOn;

    if (!dependsOn?.length) {
      return;
    }

    const latestContext = getComputeContextBuilder();
    const notifyingUpdateForId = subscriptionManager.getNotifyingUpdateForId();

    const { isContextChanged, nextContext } = getIsDependentContextChanged({
      prevContext: undefined, // no previous context on the server
      context: latestContext,
      dependsOn,
      notifyingUpdateForId,
      getNoCodeBulkComputedData,
    });

    const shouldReRender =
      dependsOn !== serverSnapshotRef.current.dependsOn ||
      nextContext !== serverSnapshotRef.current.context;

    if (isContextChanged && shouldReRender) {
      serverSnapshotRef.current = {
        context: nextContext,
        dependsOn,
      };
    }

    return serverSnapshotRef.current;
  }, [
    entityId,
    graphManager,
    getComputeContextBuilder,
    subscriptionManager,
    getNoCodeBulkComputedData,
  ]);

  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // we are calling getComputeContext here in the render cycle because where the block state changes, we need to recompute the context, with updated
  // block state. because we only track against dynamic property paths, which handles scenarios of using external entities.
  const finalContext = getComputeContextBuilder();

  return { context: finalContext };
}

export {
  type GetAppComputeContextBuilder,
  useGetAppComputeContext,
  useNonReactiveAppComputeContext,
  useReactiveAppComputeContext,
} from './useGetAppComputeContext';
export {
  type AppGlobalStateSliceForComputeContext,
  type GetInterfaceRecordStateForComputeContextArgs,
  getInterfaceRecordForComputeContext,
} from './utils/getInterfaceRecordForComputeContext';
export type { InterfaceRecordState } from './utils/getInterfaceRecordForState';
export { attachComputedNavigationToInterfaceRecord } from './utils/getInterfaceRecordForState';
